import React from "react";
import PropTypes from "prop-types";
import debounce from "lodash/debounce";
import Favorites from "lib/favorites";
import { t } from "lib/I18n";
import GtmTracker, { transformPayload } from "../lib/tracking/trackGtm";
import WebComponent from "./WebComponent";

class FavoriteButton extends React.Component {
  static propTypes = {
    authenticated: PropTypes.bool.isRequired,
    favorited: PropTypes.bool.isRequired,
    type: PropTypes.oneOf(["module", "project", "trail", "superbadge", "learningpath"]).isRequired,
    apiName: PropTypes.string.isRequired,
    contentType: PropTypes.string.isRequired,
    renderAsButton: PropTypes.bool
  };

  static defaultProps = {
    renderAsButton: false
  };

  constructor(props) {
    super(props);
    this.gtmTracker = new GtmTracker();
    this.state = {
      favorited: props.favorited
    };
  }

  getIconAssistiveText = () =>
    !this.state.favorited ? t("views.trailhead.growl.favorites.add") : t("views.trailhead.growl.favorites.remove");

  trackInteraction = (eventAction) => {
    const { type, apiName } = this.props;
    const payload = {
      eventLabel: `${type}.${apiName}`,
      eventCategory: "favorites",
      eventAction
    };

    this.gtmTracker.track(transformPayload(payload));
  };

  toggleFav = debounce(
    (event) => {
      // always unfocus the element
      if (event) {
        event.target.blur();
      }

      const { type, apiName, contentType } = this.props;
      const previousFavoriteState = this.state.favorited;

      // initialize for a desired unfavored state
      let updateFavoriteParams = { type, apiName, favorited: true, contentType };
      let trackingFn = () => this.trackInteraction("favorite");

      this.setState((prevState) => ({ favorited: !prevState.favorited }));

      // transition from favored -> unfavored
      if (previousFavoriteState) {
        updateFavoriteParams = { type, apiName, favorited: false, contentType };
        trackingFn = () => this.trackInteraction("un_favorite");
      }
      Favorites.updateFavorite(updateFavoriteParams)
        .then(trackingFn)
        .catch(() => {
          this.setState({ favorited: previousFavoriteState });
        });
    },
    1000,
    { leading: true }
  );

  loginRedirect = () => {
    this.trackInteraction("signed_out");
    document.dispatchEvent(new CustomEvent("trailhead_login"));
  };

  render() {
    const { authenticated, renderAsButton, labels } = this.props;
    const { favorited } = this.state;
    const assistiveText = this.getIconAssistiveText();
    const onClickHandler = (event) => {
      if (event) {
        event.target?.focus();
      }
      return authenticated ? this.toggleFav() : this.loginRedirect();
    };

    // Render the star via the `tds-icon` web component (sprite="trailhead"),
    // the same mechanism the full-button branch below uses. The tds-react
    // ButtonIcon resolves its glyph from its own icon set by `name` and does
    // not render a custom-sprite `iconPath`, which left this button empty.
    const iconElement = (
      <button
        type="button"
        title={assistiveText}
        aria-label={assistiveText}
        onClick={onClickHandler}
        name="favorite"
        className="slds-button slds-button_icon tds-button_icon slds-button_icon-border-filled slds-button_neutral tds-color_meteorite th-accessible-button_icon"
      >
        <WebComponent
          tagName="tds-icon"
          attrs={{
            size: "medium",
            sprite: "trailhead",
            symbol: favorited ? "star-fill" : "star-outline"
          }}
        />
      </button>
    );

    if (!renderAsButton) {
      return iconElement;
    }

    return (
      <div style={{width: "100%"}}>
        <button
          className="slds-grid slds-button slds-button_neutral"
          onClick={onClickHandler}
          aria-label={assistiveText}
          style={{ border: "#0070d2 1px solid", width: "100%", justifyContent: "center"}}
        >
          <WebComponent
            tagName="tds-icon"
            attrs={{
              size: "medium",
              sprite: "trailhead",
              symbol: favorited ? "star-fill" : "star-outline"
            }}
          />
          <span style={{ marginLeft: "8px", fontSize: "1rem" }}>
            {favorited ? labels.unfavorite : labels.favorite}
          </span>
        </button>
      </div>
    );
  }
}

export default FavoriteButton;
